// ---------------------------------------------------------------------------
// Letters play: nine tiles, one thirty-second clock, and one word.
//
// The rules here decide rounds, so none of them is a detail:
//
//   1. The player strip carries lock state and nothing else. A declared length
//      is information the server withholds until the clock stops, and leaking it
//      early would tell a player how long a word to look for. `declaredLength`
//      is null during play, and this screen renders nothing rather than a zero.
//      It lists everyone, this player included: a strip of "others" made you
//      count rows against the seat count to see whether the room was on you.
//   2. A lock is a ready signal, not a commitment. The word that gets submitted
//      is the last thing typed, so the board stays live while locked and the
//      first edit retracts the lock on the spot, a frame before the server says
//      so. Nothing on this screen may promise that an earlier word is the one
//      going in: the box shows the live answer in every state.
//   3. Everyone locked ends the round, so once that happens the clock stops
//      mattering and says so — no urgent red, no pulse.
//   4. The physical keyboard belongs to the answer only while `useKeyboardMode`
//      says so. While chat holds it, this screen's handler must not fire at all.
//   5. The lock carries `serverNow()`, not `Date.now()`. The server checks the
//      claim against the heartbeat, and a device clock that is minutes out would
//      have an honest lock thrown away.
//   6. Shuffle moves slots, not tiles. `displayOrder` is a permutation of board
//      indices and every tile keeps the index the server gave it, because the
//      answer builder consumes tiles by index: shuffling the board array itself
//      would leave a player's typed word pointing at different letters. Nothing
//      about it leaves this device — no action, no draft, no state — so nobody
//      else's board moves. The cursor is untouched by it too: the cursor is an
//      offset into the WORD and the shuffle permutes SLOTS, and the two never
//      meet.
//   7. With `lettersWordCheck` on, rule 2 is replaced rather than bent. Offering
//      a word is how a word is both checked and submitted, the server keeps the
//      longest valid one — ties going to the LATEST, so retyping at the same
//      length does replace what came before — and a lock is purely "I am ready".
//      It survives every edit and every further word, because taking it back for
//      a word that was never going to be the answer anyway would only stop the
//      round ending. The box is then a scratchpad and not a promise, so the
//      screen has to say which word is actually the answer, in a place that
//      cannot be misread: that is what the readout above the Done button is for.
//   8. A ruling is delivered at the point of entry, not in a side panel. The box
//      empties on submit and the verdict lands in the space the word just left:
//      the mark, the colour and, on a rejection, a shake. The sidebar list is a
//      record of what has been tried, and a record is not a notification.
//
//      A WORD ONLY EVER WEARS ITS OWN RULING. The verdict is cleared by the
//      next submit and by the next keystroke, never merely hidden, because the
//      box empties on submit and anything still held would take it straight
//      back over — which is what made a rejected word flash red underneath the
//      word that was about to be accepted.
//   9. A word may be offered again, as many times as there is clock for. The
//      answer is the longest offered and the LATEST among equals, so NOSE, SEND,
//      NOSE ends on NOSE; a client-side lockout was not saving a round trip, it
//      was refusing a player their own answer back. The list keeps one row per
//      word — two rows for one string would be two rows racing for the answer
//      marker — and `wordOffers.ts` is where that is enforced.
//  10. ON A PHONE THE KEYBOARD IS THE OS'S, and it comes up because the word box
//      holds an invisible input — see `AnswerKeyboardField`. Every keystroke it
//      catches goes through the same `typeLetter` a tile tap does, so a letter
//      the player does not hold is refused on a phone exactly as it is on a
//      desktop, and the field's own value is discarded rather than read.
//
//      The window listener below is what a DESKTOP types on, and it now ignores
//      any event aimed at an editable element. That is what stops the field
//      being handled twice, and it is also what stops a message being typed
//      into the chat drawer from spelling itself out on the board behind it:
//      `useKeyboardMode` is a per-component instance whose mode is only shared
//      between screens by the window-level slash shortcut, so tapping into chat
//      never told this screen anything at all.
//  11. WITH THE KEYBOARD UP, EVERYTHING NEEDED TO PLAY HAS TO BE ABOVE IT —
//      AND THERE IS ONLY ONE PHONE LAYOUT, SO THAT IS THE LAYOUT.
//
//      This used to be a second layout, swapped in when `keyboardLikely` or the
//      field's own focus said the screen was covered: the round strip hid, the
//      128px disc became a numeral and a 6px track, the shuffle and backspace
//      rows went, and the word box dropped from 78 to 64. Every one of those
//      was a real improvement under a keyboard and every one of them was also a
//      lurch, because the trigger is a reading and a reading arrives late,
//      arrives twice, and — on the report that ended this — does not come back.
//      iOS can put its keyboard away from its own control without moving focus,
//      so the screen stayed compressed with half a screen of nothing under it.
//
//      So the compressed layout is now the ONLY phone layout. Nothing on this
//      screen reads `keyboardLikely` at all; the keyboard arriving changes the
//      pinned bar's offset and nothing else, and `LettersPlayScreen.test.tsx`
//      holds the column's shape identical across the flip. The budget, on the
//      390x844 phone the tile sizes were drawn for:
//
//        room header (RoomShell)   69     nine tiles          46
//        clock row (56px disc)     56     word box            64
//        letter count              20     action bar          69
//        gaps and padding          42     ------------------------
//                                         366, into 340-440 visible
//
//      The tiles are the one thing that may not shrink: 46px is already the tap
//      target. So the clock is still what pays — but a 56px disc rather than a
//      bar, because the disc is the game's clock and the ask was to keep it if
//      it could be made to fit. It carries a numeral beside it, which the 128px
//      one does not need and a 56px one does. Twelve pixels dearer than the bar
//      it replaced, and they come out of the concessions below.
//
//      WHAT THE ONE LAYOUT KEEPS FROM THE COMPRESSED ONE, and why:
//
//        * The round strip is gone from the top of a phone. Fifty pixels above
//          the board, holding the standing and the round number, neither of
//          which plays the next thirty seconds. It is not deleted: on a phone
//          it is ordered to the BOTTOM of the column, under the roster, where
//          it costs the board nothing and is a scroll away rather than gone.
//        * The word box stays at 64 and the label above it stays gone. A 40px
//          letter sits in 64 comfortably and the caret is already blinking in
//          the only box on the screen.
//
//      WHAT COMES BACK, at full size, because a layout that never flinches can
//      afford anything BELOW the board: shuffle, Back and Clear are mounted
//      again, under the word box rather than above it, so they are behind the
//      keyboard when there is one and under the thumb when there is not, at the
//      same place in the column either way. The offered words and the roster
//      lost their `mt-auto` for the same reason — pushing them to the bottom of
//      a short column and not of a tall one is a jump with extra steps.
//  12. THE ACTION BAR IS PINNED TO THE BOTTOM OF THE VISIBLE VIEWPORT, not of
//      the window, and it is held up by `coveredHeight` rather than by
//      `bottomInset`. iOS does not resize the layout viewport for a keyboard, it
//      scrolls it, so `position: fixed; bottom: 0` parks a bar behind the keys —
//      but `bottomInset` corrects for that scroll a second time, on top of the
//      correction iOS has already made by carrying fixed elements with the
//      visual viewport, and it measures against `window.innerHeight`, which is
//      not the box a fixed element is laid out in and which moves on its own as
//      Safari's toolbars collapse. Between them that is a bar that rides up and
//      down the screen as the page scrolls, which is what was reported.
//      `coveredHeight` is the same subtraction with both scroll-dependent terms
//      removed; see the note on it in `useVisualViewport`. The bar is phone
//      only, it holds the controls that actually send something — Submit, and
//      the lock — and the flow reserves its height so it can never cover the
//      board.
//  13. THE PRESS THAT CANNOT BE TAKEN BACK IS HELD, NOT TAPPED. With open
//      answering a lock survives every edit and every further word, so `Done`
//      is the one control on a phone with no way out of it, and it sits a
//      thumb's width from `Submit`, which is pressed many times a round. It is
//      a `HoldButton` for exactly the reason the numbers round's lock is one.
//      Classic `Lock in` is left as a plain button on purpose: typing again
//      retracts it, so a stray press costs a keystroke rather than a round.
//      Enter and Space fire a `HoldButton` immediately, and the desktop keeps
//      its plain button, so no path needs a gesture a keyboard cannot make.
//
// The layout is deliberately one skeleton for every state. Locking used to swap
// the word box for a taller one, insert a banner and drop the button row, which
// reflowed the whole column on a keypress. The label, the box, the status line,
// the tile row and the button row are all mounted in every state at a fixed
// height; only their contents and their colours change.
//
// Chat is not here. The room shell owns it for every phase, so a message does
// not vanish when the round does.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { ClientRound, LetterTile as LetterTileData, PublicPlayer } from '@countdown/shared';
import {
  CheckIcon,
  ChevronRightIcon,
  CloseIcon,
  ConnectionStatus,
  HoldButton,
  Icon,
  LetterTile,
  LiveClock,
  RoundTypeChip,
  ShuffleIcon,
  StandingsStrip,
  Button,
  tileFitStyle,
} from '../../components/primitives/index.js';
import { AnswerKeyboardField } from '../../components/letters/AnswerKeyboardField.js';
import { OfferedWords } from '../../components/letters/OfferedWords.js';
import { useClock } from '../../hooks/useClock.js';
import { useKeyboardMode } from '../../hooks/useKeyboardMode.js';
import { useLettersAnswer } from '../../hooks/useLettersAnswer.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { seatedPlayers } from '../../lib/playerLookup.js';
import { identityOrder, shuffleOrder } from '../../lib/shuffleOrder.js';
import { answerOffer, findOffer, offerKey } from '../../lib/wordOffers.js';
import type { WordOffer } from '../../lib/wordOffers.js';
import { serverNow, useGameStore } from '../../store/gameStore.js';
import { prefersReducedMotion } from '../../store/settingsStore.js';
import { takeBoardHandoff } from './LettersSelectionScreen.js';
import type { RoomScreenProps } from './types.js';
import { isEditable } from '../../lib/isEditable.js';

const LETTER_KEY = /^[a-zA-Z]$/;

/** Long enough to read as one board rearranging, short enough not to cost time. */
const REORDER_MS = 220;

/**
 * The board sliding from where the selection screen left it to where it plays.
 *
 * Transform only, and nothing waits for it: the tiles are tappable and the
 * keyboard is live on the first frame, so this spends none of the thirty
 * seconds. The clock started at the ninth pick and a hold would have quietly
 * charged the player for the animation.
 */
const ENTER_MS = 260;

/**
 * The turn given to a slot that arrived without one.
 *
 * DELIBERATELY NOT `tile-flip-in`, and the comment that said it matched that
 * rule was wrong on both counts — the stylesheet's flip has always run to its
 * own number, and it is now a real two-faced 180 degree turn about the tile's
 * centre rather than a tilt. This one is a 64-degree tilt with an opacity ramp,
 * run on the face of an `available` tile, and it is only ever given to a slot
 * the selection screen never called: `slot < from.revealed` returns above. So
 * no tile turns over twice on its way here, however the stylesheet's flip is
 * built.
 */
const FLIP_MS = 260;

/**
 * The action bar's height, reserved in the flow so a pinned bar can never cover
 * the board. 52px of button, 8px of padding either side, and its own rule.
 */
const ACTION_BAR_HEIGHT = 69;

/** How long a ruling holds the box before it goes back to a plain caret. */
const VERDICT_MS = 1600;

/** What the server said about the last word that went out. */
type VerdictKind = 'rejected' | 'answer' | 'short';

interface Verdict {
  word: string;
  kind: VerdictKind;
}

function verdictOf(offer: WordOffer): VerdictKind {
  if (offer.status === 'invalid') return 'rejected';
  return offer.isAnswer ? 'answer' : 'short';
}

/**
 * What a mark means, for a reader who cannot see which one it is.
 *
 * `short` is a statement about the new rule and not a hedge: the server adopts
 * any valid word at least as long as the one it holds, ties going to the later
 * one, so a valid word it did not adopt is strictly shorter than the answer.
 */
const VERDICT_TEXT: Record<VerdictKind, string> = {
  rejected: 'not a word',
  answer: 'your answer',
  short: 'shorter than your answer',
};

/**
 * Whether a keystroke belongs to something the player is typing into.
 *
 * The window listener below is the DESKTOP's way of typing, where nothing is
 * focused. Anything editable that does have focus owns its own keys: the chat
 * box, and the phone's own answer field, which routes them into the same
 * builder from its own handlers. The same rule `useKeyboardMode` applies to the
 * slash shortcut, for the same reason.
 */

/** One player's own rearrangement of one round's board. */
interface BoardView {
  /** The round it was made on. A newer board makes it stale rather than wrong. */
  round: number | undefined;
  /** Slot to board index. Never a permutation of the tiles themselves. */
  order: number[];
  /** What the live region says, set on the press and nowhere else. */
  note: string;
}

/**
 * Where each tile sits right now, keyed by board index rather than by slot.
 *
 * A rect reads the transformed position, so a second press mid-slide measures
 * where the tile visually is and the next animation picks up from there rather
 * than snapping back to where the layout says it lives.
 */
function measureTiles(row: HTMLElement | null, order: number[]): Map<number, number> {
  const positions = new Map<number, number>();
  if (row === null) return positions;
  Array.from(row.children).forEach((child, slot) => {
    const boardIndex = order[slot];
    if (boardIndex === undefined) return;
    positions.set(boardIndex, child.getBoundingClientRect().left);
  });
  return positions;
}

/**
 * A ruling as a mark rather than a sentence.
 *
 * Three states and three readings: a red cross is refused, a green tick is the
 * word going in, a grey tick counted but did not beat what is already held.
 * The words are still here for a screen reader, because a colour and a glyph
 * are not a reading.
 */
function VerdictMark({ kind, size = 14 }: { kind: VerdictKind; size?: number }): ReactElement {
  const tone =
    kind === 'rejected' ? 'text-alert' : kind === 'answer' ? 'text-good' : 'text-ink-dim';
  return (
    <span className={`flex shrink-0 items-center ${tone}`}>
      <Icon icon={kind === 'rejected' ? CloseIcon : CheckIcon} size={size} />
      <span className="sr-only">{VERDICT_TEXT[kind]}</span>
    </span>
  );
}

/**
 * One player in the room, this one included.
 *
 * Everyone is on the list because a player checking whether the room is waiting
 * on them was reading a list that had left them out of it, and counting rows
 * against the seat count to work out where they stood.
 */
function PlayerRow({ player, locked }: { player: PublicPlayer; locked: boolean }): ReactElement {
  return (
    <div
      data-testid={`player-${player.id}`}
      // `min-w` is what makes the row above wrap rather than squeeze: a flex
      // basis of zero would fit sixteen seats onto one line at six pixels each.
      // 92px is a name at 13px plus its padding, so a line takes three on a
      // 390px phone and starts a fourth seat on the next one.
      className={`flex min-w-[92px] flex-1 flex-col items-center gap-[6px] rounded-tile border p-[10px] transition-colors duration-300 ease-out min-[900px]:min-w-0 min-[900px]:flex-row min-[900px]:gap-[10px] min-[900px]:px-[13px] min-[900px]:py-[11px] ${
        locked ? 'border-good bg-good/15' : 'border-line bg-surface-2'
      }`}
    >
      <span
        className="hidden h-2 w-2 shrink-0 rounded-full min-[900px]:block"
        style={player.color === null ? undefined : { background: player.color }}
        aria-hidden="true"
      />
      {/* `max-w-full` is what makes `truncate` do anything here, and without it
          a long name ran out of the tile on a phone. The row is a COLUMN on a
          phone, so the name is sized in the cross axis, where a flex item is
          `fit-content` and `fit-content` never goes below min-content — and
          `truncate` sets `white-space: nowrap`, which makes min-content the
          whole name. A max-width is the one thing that clamps under that floor.
          `min-w-0` is the same argument for the 900px row layout, where the
          name is the main axis and needs permission to shrink. Both are needed:
          neither breakpoint is covered by the other's fix. */}
      <span className="max-w-full min-w-0 truncate text-[13px] font-semibold min-[900px]:text-[15px]">
        {player.displayName}
      </span>
      <span
        className={`font-mono text-[10px] font-semibold tracking-[0.08em] uppercase transition-colors duration-300 ease-out min-[900px]:ml-auto ${
          locked ? 'text-good' : 'text-ink-dim'
        }`}
      >
        {locked ? 'locked' : 'thinking'}
      </span>
    </div>
  );
}

export function LettersPlayScreen({ state, send, connection }: RoomScreenProps): ReactElement {
  const round: ClientRound | undefined = state.rounds[state.currentRoundIndex];
  const tiles: LetterTileData[] = useMemo(() => round?.tiles ?? [], [round?.tiles]);

  const quality = useGameStore((store) => store.quality);
  const isDesktop = useIsDesktop();
  const keyboard = useKeyboardMode();
  const viewport = useVisualViewport();
  const reading = useClock(round?.clock ?? null, { frozen: state.serverPaused });

  // The phone's keyboard. The field is invisible and lives inside the word box;
  // this is only the screen's handle on it, for putting the keyboard away.
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const [fieldFocused, setFieldFocused] = useState(false);

  /**
   * Spread onto a control that must not take the phone keyboard away.
   *
   * Pressing anything moves focus off the invisible answer field, and focus
   * leaving the field is what puts the keyboard down — which is correct for
   * `Put the keyboard away` and wrong for everything else. Submitting a word is
   * the press a player makes many times a round, and it was costing them the
   * keyboard and then a tap on the box to get it back before the next word.
   *
   * `preventDefault` on `pointerdown` is what stops the focus shift: the press
   * still fires its `click`, the field never blurs, and the board stays up. It
   * is deliberately conditional on the field actually being focused, so a
   * player using a hardware keyboard or a desktop is untouched, and so this can
   * never suppress a focus that ought to happen.
   */
  const keepKeyboard = {
    onPointerDown: (event: { preventDefault: () => void }) => {
      if (fieldFocused) event.preventDefault();
    },
  };

  // The ruling on the last word that went out, held for a moment at the point
  // it was entered. The sidebar list is a record and a record is not a
  // notification: a greyed row in a panel is exactly what a player misses.
  //
  // Declared up here because a ruling belongs to the word that earned it and
  // nothing else may wear it, and the two things that end one — a keystroke and
  // the next submit — are both above where it is read.
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const ruled = useRef(new Set<string>());

  const answer = useLettersAnswer(tiles, (next) => {
    connection.streamWord(next);
    // Typing ENDS a ruling rather than covering it up. Hiding it while the box
    // is busy was not enough on its own: backspacing to nothing brought a
    // verdict back for a word the player had already moved on from.
    if (next !== '') setVerdict(null);
  });

  // Open answering. A room setting, so it is fixed for the whole game: this
  // never flips mid-round underneath a player.
  const wordCheck = state.settings.lettersWordCheck;
  const offers = useGameStore((store) => store.wordOffers);
  const offerWord = useGameStore((store) => store.offerWord);
  const restoreOfferedAnswer = useGameStore((store) => store.restoreOfferedAnswer);

  const own = round?.submissions[state.youId];
  const serverLocked = own?.locked === true;
  // An edit outranks the server's lock flag, because the edit is newer than the
  // state that flag came in on. The server reaches the same answer a round trip
  // later, when it takes the draft.
  //
  // With open answering that retraction is gone rather than merely unused: the
  // box is not the answer, so editing it says nothing about being ready, and
  // the server agrees — a draft does not clear a lock while the setting is on.
  const locked = serverLocked && (wordCheck || !answer.editedSinceLock);

  // Pulled out of `answer` so the effects and the callback depend on stable
  // values rather than on the object, which is new on every clock frame. A
  // listener that resubscribed sixty times a second would be a real cost.
  const { adopt, backspace, clear, deleteForward, markLocked, moveCursor, typeLetter, word } =
    answer;
  const roundIndex = round?.index;
  const ownSubmission = round?.ownSubmission ?? null;
  const ownWord =
    ownSubmission !== null && ownSubmission.kind === 'letters' ? ownSubmission.word : '';

  // A reload mid-round loses the board state but not the answer, which the
  // server still holds. Rebuilding it from the server's copy is what keeps the
  // spent tiles honest; adopt() stays silent so the rebuild cannot count as an
  // edit and retract the lock it just restored. Once per round, decided on the
  // first frame: after that the local answer is the newer of the two.
  //
  // The offered-word list cannot be rebuilt the same way, because the server
  // never had it: it keeps the answer and no record of the tries. So the answer
  // goes back into the list as a row that says where it came from, which is the
  // honest reading of a reload — the answer is safe, the tries are not.
  //
  // ONLY WHILE THE ROUND IS STILL BEING PLAYED. The crossfade renders this
  // screen a second time on its way out, as a fresh instance — new refs, an
  // empty builder — against the state that ended the round, which by then holds
  // the locked word. So the last player to press Done watched their answer
  // being typed into a box that was fading away. There is nothing to rebuild
  // for a round that is over.
  const playing = state.phase === 'letters-play';
  const hydrated = useRef<number | null>(null);
  useEffect(() => {
    if (roundIndex === undefined || !playing) return;
    if (hydrated.current === roundIndex) return;
    hydrated.current = roundIndex;
    if (ownWord.length === 0) return;
    adopt(ownWord);
    if (wordCheck) restoreOfferedAnswer(ownWord);
  }, [roundIndex, playing, ownWord, adopt, wordCheck, restoreOfferedAnswer]);

  // View state, and only view state. Nothing here is sent, nothing here enters
  // ClientGameState, and no other player's board moves because of it.
  //
  // The rearrangement carries the round it belongs to rather than being cleared
  // when the round changes: a new board simply does not match, and an unmatched
  // rearrangement reads as the order the tiles were dealt in. That is one rule
  // instead of a reset that has to fire before the first paint of the new board.
  const [view, setView] = useState<BoardView | null>(null);
  const current =
    view !== null && view.round === roundIndex && view.order.length === tiles.length ? view : null;
  const displayOrder = useMemo(
    () => current?.order ?? identityOrder(tiles.length),
    [current, tiles.length],
  );
  const orderNote = current?.note ?? '';

  const boardRef = useRef<HTMLDivElement | null>(null);
  // The before-frame of a FLIP. It only exists in the moment of the press,
  // before React has been told anything, so it is taken there and read back in
  // the layout effect below.
  const beforeReorder = useRef<Map<number, number> | null>(null);

  const shuffle = useCallback(() => {
    // `Math.random` is passed in rather than reached for, so the permutation is
    // something a test can pin. The edge is here and the rule stays there.
    const next = shuffleOrder(displayOrder, Math.random);
    beforeReorder.current = prefersReducedMotion()
      ? null
      : measureTiles(boardRef.current, displayOrder);
    setView({
      round: roundIndex,
      order: next,
      // Said once, on the press. A live region that tracked the board would
      // re-read nine letters on every keystroke.
      note: `Board now reads ${next.map((index) => tiles[index]?.letter ?? '').join(' ')}.`,
    });
  }, [displayOrder, roundIndex, tiles]);

  useLayoutEffect(() => {
    const before = beforeReorder.current;
    beforeReorder.current = null;
    const row = boardRef.current;
    if (before === null || row === null) return;

    // Transforms only, from the old position back to zero. The tiles are already
    // laid out where they belong, so nothing here can reflow the row or move the
    // controls under a thumb, and an environment without the animation API just
    // shows the new order immediately.
    Array.from(row.children).forEach((child, slot) => {
      const from = before.get(displayOrder[slot]);
      if (from === undefined) return;
      const shift = from - child.getBoundingClientRect().left;
      if (Math.abs(shift) < 1 || typeof child.animate !== 'function') return;
      child.animate(
        [{ transform: `translateX(${shift}px)` }, { transform: 'translateX(0)' }],
        { duration: REORDER_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      );
    });
  }, [displayOrder]);

  // The board arriving from the selection screen.
  //
  // The two rows never coexist — the ninth tile and the phase change land in one
  // payload — so the FLIP's before-frame was measured over there as the tiles
  // were called, and taken here. On a phone the two rows are the same 36x46 with
  // the same gap, so this is very nearly a pure translate.
  //
  // Slots that were never called on the way in turn over instead, which is how
  // the ninth tile gets the flip it could never have had on the screen that
  // dealt it. The rotation goes on the tile's inner element, because the outer
  // one carries the `perspective` that makes a rotation look like a turn.
  //
  // Runs once, on the first commit of the round, and blocks nothing.
  const entered = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (roundIndex === undefined || entered.current === roundIndex) return;
    entered.current = roundIndex;
    // Taken whatever happens next, so a capture this screen decided not to use
    // cannot be picked up by a later one.
    const board = takeBoardHandoff();
    const row = boardRef.current;
    if (row === null || prefersReducedMotion()) return;

    // A stale capture is worse than none: it would slide the board out of a
    // position it never occupied on this device.
    const from =
      board !== null && board.round === roundIndex && Date.now() - board.at < 2_000 ? board : null;
    if (from === null) return;

    Array.from(row.children).forEach((child, slot) => {
      if (typeof child.animate !== 'function') return;
      const was = from.rects[slot];
      const now = child.getBoundingClientRect();
      if (was !== undefined && was.width > 0 && now.width > 0) {
        const dx = was.left - now.left;
        const dy = was.top - now.top;
        const sx = was.width / now.width;
        const sy = was.height / now.height;
        const moved = Math.abs(dx) >= 1 || Math.abs(dy) >= 1;
        const resized = Math.abs(sx - 1) >= 0.01 || Math.abs(sy - 1) >= 0.01;
        if (moved || resized) {
          child.animate(
            [
              {
                transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`,
                transformOrigin: 'top left',
              },
              { transform: 'none', transformOrigin: 'top left' },
            ],
            { duration: ENTER_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
          );
        }
      }

      if (slot < from.revealed) return;
      const face = child.firstElementChild;
      if (face === null || typeof face.animate !== 'function') return;
      face.animate(
        [
          { transform: 'rotateX(64deg)', opacity: 0.55 },
          { transform: 'rotateX(0deg)', opacity: 1 },
        ],
        { duration: FLIP_MS, easing: 'ease-out', delay: ENTER_MS - 80, fill: 'backwards' },
      );
    });
  }, [roundIndex]);

  // The word the server says is this player's, and the row it came in on. Null
  // until something valid has been offered.
  const heldAnswer = wordCheck ? answerOffer(offers) : null;
  const answerWord = heldAnswer?.word ?? '';
  // The row for whatever is in the box right now, if it has already been sent.
  // It marks the line under the box; it does not gate the button, because a
  // word may be offered again.
  const boxOffer = wordCheck ? findOffer(offers, word) : undefined;
  const canOffer = wordCheck && word.length > 0;

  const offer = useCallback(() => {
    const candidate = offerKey(word);
    if (candidate === '') return;
    // A repeat goes. The latest of two equally long words is the one the server
    // keeps, so NOSE, SEND, NOSE has to land back on NOSE — a client that
    // refused the second NOSE was not saving a round trip, it was taking the
    // player's answer away. The list gives the word its own row back rather
    // than opening a second one.
    const actionId = crypto.randomUUID();
    // Whatever the last word was ruled, it is over: this press is a new
    // question. Without this the box keeps the old ruling's colour, and since
    // the box empties on send, the moment it does the previous verdict takes it
    // over again — a rejected word flashing red underneath the word that was
    // about to be accepted.
    setVerdict(null);
    // Recorded before it goes, so the row is on screen for the round trip
    // rather than appearing when the ruling lands.
    offerWord(candidate, actionId);
    send({
      type: 'submit-word',
      word: candidate,
      actionId,
      clientSubmittedAt: serverNow(),
    });
    // The box is emptied by the send, not left holding the word it just asked
    // about. It is also where the ruling lands, and a word cannot be both the
    // question and the answer to it. Nothing is lost: the word is on the list,
    // the answer is the server's, and this is a scratchpad.
    clear();
  }, [word, offerWord, send, clear]);

  // A ruling is an EVENT — it lands on the socket — rather than something
  // derived from the list, so it is watched at the store rather than recomputed
  // from a rendered prop. The list is the wrong shape to read it out of anyway:
  // with two words in flight the second can be answered first, so "the newest
  // ruling" is the row that has not been seen before and never the last row.
  useEffect(() => {
    if (!wordCheck) return;
    const seen = ruled.current;
    // Whatever was already settled when this screen mounted has been seen. A
    // verdict is for a word this player just sent, not for the list they
    // arrived holding. Pending rows are deliberately left unseen: their ruling
    // is exactly what this is waiting for.
    for (const row of useGameStore.getState().wordOffers) {
      if (row.status !== 'pending') seen.add(row.actionId);
    }

    return useGameStore.subscribe((next, previous) => {
      if (next.wordOffers === previous.wordOffers) return;
      let latest: WordOffer | null = null;
      for (const row of next.wordOffers) {
        // A restored answer is the server telling us what it already held, not
        // a ruling on anything this player just did.
        if (row.status === 'pending' || row.restored) continue;
        if (seen.has(row.actionId)) continue;
        seen.add(row.actionId);
        latest = row;
      }
      if (latest !== null) setVerdict({ word: latest.word, kind: verdictOf(latest) });
    });
  }, [wordCheck]);

  useEffect(() => {
    if (verdict === null) return;
    const timer = window.setTimeout(() => setVerdict(null), VERDICT_MS);
    return () => window.clearTimeout(timer);
  }, [verdict]);

  // A rejection has to be felt as well as seen, because the one thing a player
  // does after submitting is look away from the box and start hunting again.
  const wordBoxRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (verdict === null || verdict.kind !== 'rejected') return;
    const box = wordBoxRef.current;
    if (box === null || typeof box.animate !== 'function' || prefersReducedMotion()) return;
    box.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-8px)' },
        { transform: 'translateX(7px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(2px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 320, easing: 'ease-out' },
    );
  }, [verdict]);

  // The answer changing under a player who is already typing the next word is
  // the one event on this screen they are not looking at, so it moves.
  const answerRef = useRef<HTMLDivElement | null>(null);
  const lastAnswer = useRef('');
  useEffect(() => {
    const previous = lastAnswer.current;
    lastAnswer.current = answerWord;
    if (answerWord === '' || answerWord === previous) return;
    const readout = answerRef.current;
    if (readout === null || typeof readout.animate !== 'function' || prefersReducedMotion()) return;
    readout.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.03)' }, { transform: 'scale(1)' }],
      { duration: 240, easing: 'ease-out' },
    );
  }, [answerWord]);

  const lockIn = useCallback(() => {
    // With open answering the lock is a ready signal and nothing else, so it
    // carries the word the SERVER holds rather than whatever is in the box.
    // Sending the box would be the original incident rebuilt: a player who
    // locked with a longer word already banked and a half-typed one on screen
    // would overwrite the good answer with the scratchpad.
    const declared = wordCheck ? answerWord : word;
    // Off, a lock with nothing in it is meaningless. On, it is a player saying
    // they are done having found nothing, which is a real thing to say.
    if (declared.length === 0 && !wordCheck) return;
    send({
      type: 'lock-letters',
      word: declared,
      actionId: crypto.randomUUID(),
      clientLockedAt: serverNow(),
    });
    markLocked();
  }, [answerWord, word, wordCheck, markLocked, send]);

  useEffect(() => {
    // While chat holds the keyboard every keystroke is chat's, including the
    // letters. Nothing here may fire, not even a silent no-op.
    if (keyboard.mode !== 'answer') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Whatever is focused owns its own keys. This listener is how a DESKTOP
      // types, with nothing focused at all, and it must not also read the keys
      // going into the phone's answer field — which routes them into this same
      // builder itself, so handling them here as well would double every
      // letter. It is the same guard that keeps a message being typed in the
      // chat drawer from spelling itself out on the board behind it.
      if (isEditable(event.target)) return;
      if (event.key === 'Backspace') {
        event.preventDefault();
        backspace();
        return;
      }
      if (event.key === 'Delete') {
        event.preventDefault();
        deleteForward();
        return;
      }
      // The insertion point, so a wrong second letter costs one keystroke
      // rather than backspacing over everything after it. Both arrows are
      // unclaimed on this screen — nothing here scrolls, and no control on it
      // uses an arrow — and preventing the default stops the page panning
      // sideways under the board on a trackpad.
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveCursor(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveCursor(1);
        return;
      }
      if (event.key === 'Enter') {
        // ENTER IS THE WORD GOING IN, WHATEVER WAS PRESSED LAST.
        //
        // It used to stand aside for a focused button — `event.target
        // instanceof HTMLButtonElement` and return — on the grounds that the
        // control with focus wins, which is what the platform promises. The
        // platform's promise is about a button somebody chose to focus, and a
        // click leaves focus behind on Chrome and on Windows whether anybody
        // meant it or not: pressing Shuffle and then Enter shuffled the board
        // again, at the moment the player had finished rearranging it and
        // wanted the word sent. Isaac reported it on Shuffle; Back and Clear
        // had it too, and the deference was doing the same thing there — Enter
        // repeating the last press instead of ending the word.
        //
        // So Enter is taken here in every case, and `preventDefault` is what
        // keeps that from being a press spent twice: a plain button's
        // activation on Enter is a DEFAULT ACTION, run after the event has
        // finished bubbling, so cancelling it at the window cancels the click
        // the focused button would otherwise have made. Space is untouched and
        // still activates a focused button, which is what keeps every control
        // on this screen reachable from a keyboard.
        //
        // A control that answers Enter with a handler of its own is a
        // different thing and says so by preventing the default itself —
        // `HoldButton` does, because Enter is how a keyboard performs a hold
        // it cannot perform. That press is already spoken for and must not
        // also send a word.
        if (event.defaultPrevented) return;
        event.preventDefault();
        // Enter is the frequent action in each mode: with open answering that
        // is offering a word, which happens many times a round, rather than
        // declaring yourself done, which happens once. Deliberately not Space —
        // Space is the conundrum buzz a round later in the same sitting, and a
        // reflex taught here would cost somebody that buzz.
        if (wordCheck) offer();
        else lockIn();
        return;
      }
      // Typing while locked is allowed and retracts the lock. The rule is that
      // the last word typed is the word submitted, so the keyboard can never go
      // dead on a player who has changed their mind.
      if (LETTER_KEY.test(event.key)) typeLetter(event.key);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keyboard.mode, backspace, deleteForward, moveCursor, typeLetter, lockIn, offer, wordCheck]);

  // Unreachable through RoomPage, which routes here by phase. It exists because
  // the phase router's screens must always return an element.
  if (round === undefined) {
    return <p className="p-6 text-ink-dim">Waiting for the round.</p>;
  }

  const roster = seatedPlayers(state);
  // Locked is this player's own view of their own lock, so an edit counts here
  // the moment it lands rather than a round trip later. Which is also why the
  // roster row for this player reads it rather than the submission map: the
  // server agrees a round trip later.
  const lockedBy = (player: PublicPlayer) =>
    player.id === state.youId ? locked : round.submissions[player.id]?.locked === true;
  const everyoneIn = roster.every(lockedBy);
  // 56 rather than 128 on a phone, in every state. The disc is the game's clock
  // and it stays a disc — that was the ask — but it is the only thing on the
  // screen that can give a keyboard sixty pixels without taking anything away,
  // because the seconds are printed beside it and the wedge angle is the shape
  // rather than the reading. 56 is a size in `PieClock`'s own tick table, so the
  // ticks sit where they were drawn to rather than being interpolated.
  const clockSize = isDesktop ? 200 : 56;
  const tileSize = isDesktop ? 'board' : 'phone';
  const boardGap = isDesktop ? 9 : 5;
  // Nine `phone` tiles and their gaps are 364px, which is wider than a 390px
  // viewport once the page's own padding is taken off, and wider still on
  // anything narrower. Fitted, the row takes its share of the width instead —
  // capped at the designed 36px, so on the phone this was drawn for nothing
  // changes at all, and on a smaller one the board stops running off the side.
  // Desktop keeps the fixed table it has always had.
  const fitTiles = !isDesktop;
  // Nothing to rearrange before the board is dealt, and nothing worth
  // rearranging once the answer is in: a locked board is a record of what the
  // player did. `locked` here is the local reading, so a player who has just
  // typed again and retracted their lock gets the button straight back.
  //
  // With open answering a locked board is not a record of anything — the whole
  // point is that a locked player carries on hunting — so the button stays.
  const canShuffle = tiles.length > 1 && (wordCheck || !locked);
  const length = word.length;
  const typingHere = keyboard.mode === 'answer';
  const cursor = answer.cursor;

  const clockLabel = everyoneIn
    ? 'everyone is in'
    : `${reading.seconds} seconds left`;

  // Nothing is left to race for once everyone is in, so the clock stops shouting
  // even if it is inside the last twenty percent.
  const clockVariant = reading.frozen
    ? 'frozen'
    : reading.urgent && !everyoneIn
      ? 'alert'
      : 'normal';

  // The ruling takes the box over once the word it ruled on has left it, which
  // is immediately: the box empties on submit. Anything typed since outranks
  // it, because what the player is doing beats what they just did.
  const showVerdict = wordCheck && verdict !== null && length === 0;
  const verdictKind = showVerdict ? verdict.kind : null;

  // The right-hand end of the line under the box. With open answering it is a
  // mark and not a sentence — the box has already said it in colour — and it is
  // only there at all for a string that has been ruled on and typed again.
  const boxMark: VerdictKind | 'pending' | null = !wordCheck
    ? null
    : boxOffer === undefined
      ? null
      : boxOffer.status === 'pending'
        ? 'pending'
        : verdictOf(boxOffer);

  // What is left after the marks, which is very little. With open answering,
  // nothing: the mark says it. With the setting off, the two things the screen
  // cannot show — that a word is not checked until the clock stops, and that a
  // lock can be taken back by typing.
  //
  // "type or tap the tiles" went with the caret already blinking where the
  // letters land, and "everyone is in" was the fourth place on the screen
  // saying so.
  const boxNote = !wordCheck && locked
    ? 'tap or type to change it'
    : !wordCheck && length > 0
      ? 'checked at the end'
      : '';

  return (
    // The shell's crossfade is opted out of for the seam that arrives here, and
    // only that one: it has a shared element rather than a dissolve. The board is
    // the same nine tiles it was a frame ago and it travels, so fading a copy of
    // it out over a copy of it in would be two boards at once.
    //
    // The opt-out is named in `CROSSFADE_EXEMPT` in PhaseCrossfade, not marked
    // on this element. A `data-no-crossfade` attribute lived here for a while
    // with nothing reading it, which is worse than no opt-out at all: it says
    // the boundary is handled while the dissolve runs anyway.
    <div className="flex min-h-full flex-col">
      {/* The round strip, not a second header. `RoomShell` puts `AppHeader` above
          every room screen and that bar owns the only rule on the page; this one
          carried a `border-b` of its own, so a player arriving in a round saw two
          full-width bars stacked with a strip of chrome between them. Same row,
          no rule — it now reads as the top of the board rather than as a second
          masthead, which is how the selection and numbers screens already had it. */}
      {/* The standing and the round counter are what the round looks like from
          outside it, and none of them plays the next thirty seconds. On a phone
          they are ORDERED LAST rather than hidden: fifty pixels above the board
          is fifty pixels the tiles and the box need with a keyboard up, and a
          strip that is present in one state and absent in the other is the
          lurch this screen was rebuilt to stop. Last in the column, under the
          roster, is a scroll away in both states and the same place in both.
          Desktop is untouched — it has the room, and the strip belongs on top
          of a play area nothing is covering. */}
      <header className="order-last flex items-center gap-2 px-3 py-3 min-[900px]:order-none min-[900px]:px-6 min-[900px]:py-4">
        <RoundTypeChip type={round.type} size="sm" />
        <div className="min-w-0 flex-1 overflow-hidden">
          <StandingsStrip state={state} showRoundCounter />
        </div>
        <ConnectionStatus quality={quality} compact />
      </header>

      <div
        // 12px of side padding rather than 16, which is not a nudge: nine
        // `phone` tiles and their gaps are 364px, and 390 less 24 is 366. At 16
        // the board on the very phone it was drawn for had to shrink to fit,
        // and on a 375px one it fell under the 44px tap target.
        className="flex flex-1 flex-col gap-[10px] px-3 pt-3 min-[900px]:grid min-[900px]:grid-cols-[1fr_300px] min-[900px]:gap-0 min-[900px]:p-0"
      >
        <main className="flex flex-col items-center gap-[10px] min-[900px]:justify-center min-[900px]:gap-7 min-[900px]:p-7">
          {/* `contents` on phone so the clock, the word and the tiles order as one
              column; a flex row on desktop, where the clock sits beside the word. */}
          <div className="contents min-[900px]:flex min-[900px]:items-center min-[900px]:gap-10">
            {/* ONE CLOCK ROW, IN EVERY STATE. On a phone it is a 56px disc, the
                seconds beside it, the answer, and the way out of the keyboard;
                on a desktop it is the 200px disc on its own, exactly as it was.
                Nothing in here is conditional on the keyboard, which is the
                whole repair: this row is the same height and the same shape
                whether the screen is covered or not. */}
            <div
              data-testid="clock-row"
              className="order-1 flex w-full items-center gap-[10px] min-[900px]:order-none min-[900px]:w-auto"
            >
              {/* The dimming lives on a wrapper rather than on the clock, because
                  the urgent pulse animates the clock's own filter and the two
                  would fight over it. */}
              <div
                className={`shrink-0 transition-opacity duration-500 ease-out ${
                  everyoneIn ? 'opacity-60' : 'opacity-100'
                }`}
              >
                {/* `LiveClock` and not `PieClock`: the disc subscribes to the
                    clock itself, so the frame loop that moves the wedge does not
                    re-render this screen. `reading` above is the same
                    arithmetic at four times a second, which is the right rate
                    for everything else on the page. */}
                <LiveClock
                  clock={round?.clock ?? null}
                  frozen={state.serverPaused}
                  size={clockSize}
                  variant={clockVariant}
                  label={clockLabel}
                />
              </div>

              {/* The seconds, BESIDE the disc and never inside it. `PieClock`'s
                  rule is that the disc is the readout and the numerals live in
                  its label, and that rule is written for a disc big enough to
                  read an angle off: at 200px a sixth of a turn is unmistakable,
                  at 56px it is a hair. So the phone gets the number back, next
                  to the disc rather than in it, and `aria-hidden` because the
                  disc's own label already says the same sentence.

                  `text-ink` rather than the wedge colour: urgency is already
                  carried by the disc turning red and pulsing, and a second
                  element saying it would be a colour pair the palette has not
                  been solved for. */}
              {isDesktop ? null : (
                <span
                  data-testid="clock-seconds"
                  aria-hidden="true"
                  className={`tabular w-[26px] shrink-0 text-right font-mono text-[20px] leading-none font-bold transition-opacity duration-500 ease-out ${
                    everyoneIn ? 'opacity-60' : 'opacity-100'
                  }`}
                >
                  {reading.seconds}
                </span>
              )}

              {/* The answer, on a phone, in the one row that is above the
                  keyboard by construction. It is here rather than in the block
                  under the board because there can only be one of it: two
                  readouts of the same word would be two things claiming to be
                  the answer. Desktop keeps the block, where there is room for
                  the score beside it. */}
              {wordCheck && !isDesktop ? (
                <div
                  // The same ref as the desktop block's readout below. Only one
                  // of the two is ever mounted, and the pulse when the answer
                  // changes underneath a player who is typing the next word is
                  // needed on a phone more than anywhere: it is the one event on
                  // the screen they are not looking at.
                  ref={answerRef}
                  data-testid="answer-readout"
                  data-has-answer={answerWord.length > 0 || undefined}
                  className={`flex h-9 min-w-0 flex-1 items-center gap-[6px] rounded-chip border px-[10px] ${
                    answerWord.length > 0 ? 'border-good bg-good/15' : 'border-line bg-surface-2'
                  }`}
                >
                  {answerWord.length === 0 ? (
                    <span className="truncate text-[12px] leading-none text-ink-dim">
                      Nothing yet
                    </span>
                  ) : (
                    <>
                      <Icon icon={CheckIcon} size={13} className="text-good" />
                      <span className="truncate font-mono text-[13px] leading-none font-bold tracking-[0.06em] text-good">
                        {answerWord}
                      </span>
                      {/* The score comes with it. It used to be down in the
                          block under the board, which is behind the keyboard
                          for most of a round, and the offered-words list is the
                          other place it lives — also behind the keyboard. The
                          word is what shrinks if the row runs out of room; the
                          number is short and fixed. */}
                      {heldAnswer !== null && heldAnswer.score !== null ? (
                        <span className="tabular ml-auto shrink-0 font-mono text-[11px] leading-none font-semibold text-good">
                          {`${heldAnswer.score} points`}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}

              {/* Classic rules have no answer readout, and without something
                  that grows the row would pack the dismiss control against the
                  seconds. One spacer, so the two settings share a row shape. */}
              {wordCheck || isDesktop ? null : <span aria-hidden="true" className="flex-1" />}

              {/* The keyboard covers the tiles, so there has to be a way to put
                  it down that is not "submit something".

                  ITS SLOT IS ALWAYS THERE AND THE BUTTON IS NOT. It used to be
                  mounted only while the field held focus, which moved the row
                  sideways on the frame the keyboard arrived — a small version of
                  the reflow this whole layout was rebuilt to stop. So the space
                  is reserved in every state and the control is made invisible
                  and unreachable when there is no keyboard to put away, rather
                  than offering a button that would say something untrue.

                  The tap itself is what dismisses the keyboard — focus leaves
                  the field the moment anything else is pressed — so this works
                  even though pressing it is what hides it again. `blur()` is the
                  belt to that braces, and a no-op when the tap has already done
                  it. */}
              {isDesktop ? null : (
                <button
                  type="button"
                  aria-label="Put the keyboard away"
                  aria-hidden={fieldFocused ? undefined : true}
                  tabIndex={fieldFocused ? undefined : -1}
                  onClick={() => fieldRef.current?.blur()}
                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-chip border border-line text-ink-muted ${
                    fieldFocused ? '' : 'invisible'
                  }`}
                >
                  <Icon icon={ChevronRightIcon} size={18} className="rotate-90" />
                </button>
              )}
            </div>

            <div className="order-3 flex w-full flex-col gap-[10px] min-[900px]:order-none min-[900px]:w-auto">
              {/* Open answering makes the box a place to try things rather than
                  a declaration, and the label is the first thing that has to
                  say so. What the player's answer actually is has its own
                  readout, in the clock row on a phone and in its own block on a
                  desktop.

                  Desktop only. On a phone the label is twenty-two pixels the
                  board needs, and it is saying nothing the screen does not
                  already say: the box is the only thing a caret is blinking in,
                  the answer readout is one row above it, and the field carries
                  the same words as its accessible name. */}
              {isDesktop ? (
                <div className="label-mono">{wordCheck ? 'try a word' : 'your word'}</div>
              ) : null}

              <div
                ref={wordBoxRef}
                data-testid="word-box"
                data-locked={locked || undefined}
                data-verdict={verdictKind ?? undefined}
                // Fixed on desktop rather than min-width: a nine letter word is
                // the widest this can get, it fits, and a box that grew as the
                // word did would push the clock sideways letter by letter.
                //
                // `relative`, because the phone's answer field is stretched over
                // it: the box IS the tap target that puts the keyboard up.
                //
                // 64 on a phone in every state. It was 78 with no keyboard and
                // 64 with one, and 64 is the one that had to be right: a 40px
                // letter sits in it with room to spare, and the fourteen pixels
                // are the difference between the tiles clearing a keyboard on a
                // short phone and not.
                className={`relative flex h-[64px] items-center justify-center gap-[3px] rounded-card border transition-colors duration-300 ease-out min-[900px]:h-[104px] min-[900px]:w-[520px] min-[900px]:justify-start min-[900px]:px-6 ${
                  fieldFocused ? 'ring-2 ring-blue-400' : ''
                } ${
                  // The wash is swapped rather than layered: two tints over one
                  // another composite deeper than the palette was solved for.
                  //
                  // Never green for a lock while open answering is on. Green is
                  // what the answer readout uses, and a box that borrowed it
                  // would be claiming to be the answer.
                  verdictKind === 'rejected'
                    ? 'border-alert bg-alert/15'
                    : verdictKind === 'answer'
                      ? 'border-good bg-good/15'
                      : locked && !wordCheck
                        ? 'border-good bg-blue-700/30'
                        : 'border-line-strong bg-blue-700/30'
                }`}
              >
                {/* The phone's keyboard, and nothing else: it holds no text,
                    its value is discarded, and every key it catches is routed
                    through the same builder the tiles are. Not rendered on a
                    desktop, where typing must keep working with nothing
                    focused at all. */}
                {isDesktop ? null : (
                  <AnswerKeyboardField
                    fieldRef={fieldRef}
                    onLetter={typeLetter}
                    onBackspace={backspace}
                    onDeleteForward={deleteForward}
                    onMoveCursor={moveCursor}
                    onEnter={wordCheck ? offer : lockIn}
                    onFocusChange={setFieldFocused}
                    // **Never `done`.** iOS treats `enterKeyHint="done"` as an
                    // instruction to put the keyboard away, so pressing it
                    // submitted the word AND dismissed the board — and the next
                    // word cost a tap on the box before a single letter could
                    // be typed. Isaac reported it; there is a matching
                    // `keepKeyboard` on the buttons below, because a tap moves
                    // focus for the same net effect by a different route.
                    //
                    // `send` is also the truer label of the two. A lock is not
                    // the end of anybody's typing: typing again retracts it,
                    // deliberately, so that the last word typed is the word
                    // submitted.
                    enterKeyHint="send"
                    label={wordCheck ? 'Try a word' : 'Your word'}
                  />
                )}

                {showVerdict ? (
                  // The word that was just ruled on, where it was typed, in the
                  // one place a player cannot be looking away from. It goes the
                  // moment anything is typed over it.
                  <span data-testid="verdict" className="flex items-center gap-3 overflow-hidden">
                    <VerdictMark kind={verdict.kind} size={isDesktop ? 32 : 24} />
                    <span
                      className={`truncate text-[40px] leading-none font-extrabold tracking-[0.06em] min-[900px]:text-[56px] ${
                        verdict.kind === 'rejected'
                          ? 'text-alert'
                          : verdict.kind === 'answer'
                            ? 'text-good'
                            : 'text-ink-dim'
                      }`}
                    >
                      {verdict.word}
                    </span>
                  </span>
                ) : (
                  <>
                    {/* Split at the insertion point rather than rendered whole,
                        so the caret sits inside the word when the cursor has
                        been moved. Two spans and a caret in every state: the
                        second is empty while the cursor is at the end, which is
                        where it starts and where it spends most of a round. */}
                    <span className="text-[40px] leading-none font-extrabold tracking-[0.06em] min-[900px]:text-[56px]">
                      {word.slice(0, cursor)}
                    </span>
                    {/* The caret is where the keystrokes are going. It blinks
                        while the answer holds the keyboard and sits still when
                        chat has taken it, and it keeps its width in both, so
                        nothing moves. */}
                    <span
                      data-testid="caret"
                      className={`mx-[3px] h-10 w-[3px] bg-blue-200 transition-opacity duration-300 ease-out min-[900px]:h-14 min-[900px]:w-1 ${
                        locked && !wordCheck
                          ? 'opacity-40'
                          : typingHere
                            ? 'animate-pulse'
                            : 'opacity-50'
                      }`}
                      aria-hidden="true"
                    />
                    <span
                      data-testid="after-cursor"
                      className="text-[40px] leading-none font-extrabold tracking-[0.06em] min-[900px]:text-[56px]"
                    >
                      {word.slice(cursor)}
                    </span>
                  </>
                )}
              </div>

              {/* One fixed-height line in every state, so the count, the hint and
                  the mark swap without moving the button row. */}
              <div className="flex h-5 items-center justify-between gap-3 font-mono text-xs leading-none font-medium">
                <span className={length === 0 ? 'text-ink-dim' : 'text-ink-muted'}>
                  {`${length} ${length === 1 ? 'letter' : 'letters'}`}
                </span>
                <span data-testid="box-note" className="flex items-center gap-2 text-ink-dim">
                  {boxNote}
                  {boxMark === 'pending' ? (
                    <span className="flex items-center">
                      <span
                        className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-line-strong"
                        aria-hidden="true"
                      />
                      <span className="sr-only">checking</span>
                    </span>
                  ) : boxMark !== null ? (
                    <VerdictMark kind={boxMark} />
                  ) : null}
                </span>
              </div>

              {/* Submitting IS checking, which is why there is no second
                  control asking whether a word is a word: two controls over one
                  string could disagree about it, and with the longest valid word
                  winning by itself there is nothing to weigh up about offering
                  one. An empty box is the only thing that disables it — a word
                  already tried may be tried again, and the answer follows the
                  latest one. */}
              {wordCheck && isDesktop ? (
                <Button
                  variant="primary"
                  size="lg"
                  disabled={!canOffer}
                  onClick={offer}
                  className="w-full min-[900px]:w-[520px]"
                >
                  Submit
                </Button>
              ) : null}
            </div>
          </div>

          {/* `tile-fit` is the container `--tile-fit` is measured against, so it
              goes on the row itself, which takes its width from `w-full` rather
              than from the nine tiles inside it. See `tileFitStyle`. */}
          <div
            ref={boardRef}
            data-testid="tile-row"
            data-board
            className={
              fitTiles
                ? 'tile-fit order-2 flex w-full justify-center gap-[5px] min-[900px]:order-none min-[900px]:gap-[9px]'
                : 'order-2 flex gap-[5px] min-[900px]:order-none min-[900px]:gap-[9px]'
            }
            style={fitTiles ? tileFitStyle(tileSize, tiles.length, boardGap) : undefined}
          >
            {displayOrder.map((index) => {
              const tile = tiles[index];
              if (tile === undefined) return null;
              return (
                <LetterTile
                  // The board index, not the slot. Keying by position would have
                  // React rewrite nine tiles in place on a shuffle rather than
                  // move them, which loses both the animation and the identity
                  // this whole feature rests on.
                  key={index}
                  letter={tile.letter}
                  // Spent and unspent stay legible while locked. A board of nine
                  // padlocks hid the one thing the player wanted to check.
                  state={answer.isUsed(index) ? 'used' : 'available'}
                  size={tileSize}
                  fit={fitTiles}
                  onClick={answer.isUsed(index) ? undefined : () => answer.tap(index)}
                />
              );
            })}
          </div>

          {/* Mounted in every state at the same height, disabled rather than
              removed, so nothing below it moves. It used to sit directly under
              the board and to be dropped whenever a keyboard was up; it is now
              BELOW the word box, in both states, which is what lets it stay.
              Everything above the box has to clear a keyboard, everything below
              it does not, and a shuffle is not what the next thirty seconds are
              for once the typing has started. Two rows clear of the lock, which
              is the one thing it must never be mistaken for. */}
          <div className="order-5 flex items-center justify-center gap-3 min-[900px]:order-none">
            <Button
              variant="outline"
              size="sm"
              disabled={!canShuffle}
              onClick={shuffle}
              aria-label="Shuffle the tiles"
            >
              <Icon icon={ShuffleIcon} size={16} />
              Shuffle
            </Button>

            {/* The reorder, said once. A region that echoed the board would talk
                over every keystroke instead. */}
            <span data-testid="order-note" className="sr-only" aria-live="polite">
              {orderNote}
            </span>
          </div>

          {/* Under the word box rather than above it, and mounted in every
              state. With a keyboard up this row is behind the keys, which is
              exactly where it should be — the keyboard in front of the player
              has a backspace on it — and without one it is under the thumb. It
              is the same row in the same place either way, which is the only
              reason it could come back at all. */}
          <div className="order-4 flex w-full gap-[10px] min-[900px]:order-none min-[900px]:w-auto min-[900px]:gap-3">
            <Button
              variant="outline"
              size="lg"
              disabled={length === 0}
              onClick={answer.backspace}
              // The lock has moved to the pinned bar on a phone, in both
              // settings, so these two take the whole row rather than leaving a
              // hole where a button used to be.
              className="flex-1 min-[900px]:w-[120px] min-[900px]:flex-none"
            >
              {isDesktop ? 'Backspace' : 'Back'}
            </Button>
            <Button
              variant="outline"
              size="lg"
              disabled={length === 0}
              onClick={answer.clear}
              className="flex-1 min-[900px]:w-[100px] min-[900px]:flex-none"
            >
              Clear
            </Button>
            {/* Same slot, same size, in both states. Locked is a state rather
                than an action, so it is not a button: the way back out is to
                keep playing, which the line above the row says.

                With open answering this control moves down to sit beside the
                answer it declares, because there it can never be read as
                locking in whatever happens to be in the box. */}
            {wordCheck || !isDesktop ? null : locked ? (
              <div
                data-testid="lock-state"
                className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[11px] border-2 border-good bg-good/15 text-[16px] leading-none font-bold text-good min-[900px]:w-[220px] min-[900px]:flex-none"
              >
                <Icon icon={CheckIcon} size={18} />
                Locked in
              </div>
            ) : (
              // One label. It carried the letter count, which is already on the
              // line above the box and changed the button on every keystroke.
              <Button
                variant="primary"
                size="lg"
                disabled={length === 0}
                onClick={lockIn}
                className="flex-1 min-[900px]:w-[220px] min-[900px]:flex-none"
              >
                Lock in
              </Button>
            )}
          </div>

          {/* The answer, and the control that declares the player done with it,
              in one row. They belong together: the incident this whole setting
              exists to end was a player being scored a word they had abandoned,
              and the fix is that the thing going in is written next to the
              button that says "I am ready" — not inferred from the box, which
              by now holds the next thing they are trying.

              DESKTOP ONLY, now that there is one phone layout. On a phone the
              answer is in the clock row and the control is in the pinned bar,
              which is where both of them have to be when a keyboard is up —
              and there can only be one of each. This block used to appear on a
              phone whenever the screen was not covered, which is precisely the
              swap that made the column jump. */}
          {wordCheck && isDesktop ? (
            <div className="order-6 flex w-full flex-col gap-[10px] min-[900px]:order-none min-[900px]:w-[520px]">
              <div className="label-mono">your answer</div>
              <div className="flex items-center gap-[10px]">
                <div
                  ref={answerRef}
                  data-testid="answer-readout"
                  data-has-answer={answerWord.length > 0 || undefined}
                  className={`flex h-[52px] flex-1 items-center gap-2 rounded-[11px] border px-3 ${
                    answerWord.length > 0 ? 'border-good bg-good/15' : 'border-line bg-surface-2'
                  }`}
                >
                  {answerWord.length === 0 ? (
                    <span className="text-[13px] leading-none text-ink-dim">Nothing yet.</span>
                  ) : (
                    <>
                      <Icon icon={CheckIcon} size={16} className="text-good" />
                      <span className="truncate font-mono text-[18px] leading-none font-bold tracking-[0.06em] text-good">
                        {answerWord}
                      </span>
                      {heldAnswer !== null && heldAnswer.score !== null ? (
                        <span className="tabular ml-auto font-mono text-[11px] leading-none font-semibold text-good">
                          {`${heldAnswer.score} points`}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>

                {/* On a phone this control is in the pinned bar instead, where
                    a thumb can reach it with a keyboard up. Two of it would be
                    two things claiming to end the round. */}
                {!isDesktop ? null : locked ? (
                  // Pressed, and it has to look pressed: the same word in the
                  // same slot, wearing a tick and a doubled green edge where a
                  // moment ago there was a blue button. The wash stays at /15
                  // and the text stays `text-good` — `good` is a foreground
                  // colour in this palette and never a fill behind text, so
                  // "filled green" is bought with the border and the mark
                  // rather than with an unsolved colour.
                  //
                  // The label does not change when the last player arrives. It
                  // was "All in" for the quarter second before the round ended,
                  // which is a state nobody has time to read and everybody sees
                  // move.
                  <div
                    data-testid="lock-state"
                    className="flex h-[52px] w-[120px] shrink-0 items-center justify-center gap-2 rounded-[11px] border-2 border-good bg-good/15 text-[15px] leading-none font-bold text-good"
                  >
                    <Icon icon={CheckIcon} size={18} />
                    Done
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={lockIn}
                    className="w-[120px] shrink-0"
                    // Spelled out, because "Done" alone does not say what is
                    // being declared and this is the press that ends a round.
                    aria-label={
                      answerWord.length > 0 ? `Done, with ${answerWord}` : 'Done, with no answer'
                    }
                  >
                    Done
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </main>

        {/* NO `mt-auto`. It used to push this block to the bottom of the phone's
            column and to be dropped as soon as a keyboard was up, which is a
            hundred-odd pixels of movement bought for nothing: the block is below
            everything the round needs either way, so where it sits when there is
            spare screen is a matter of taste, and taking a jump for it is not.
            It sits directly under the buttons in both states now. Whatever does
            not fit is a scroll away rather than gone. */}
        <aside className="flex flex-col gap-[10px] min-[900px]:mt-0 min-[900px]:min-h-0 min-[900px]:gap-0 min-[900px]:border-l min-[900px]:border-line">
          {/* Below everything the thumb uses, and a fixed height whether it
              holds nothing or twenty words, so a row landing in it cannot move
              the tiles, the box or the buttons. It scrolls inside itself
              instead. */}
          {wordCheck ? (
            <div className="min-[900px]:border-b min-[900px]:border-line min-[900px]:p-[18px]">
              <OfferedWords offers={offers} />
            </div>
          ) : null}

          <div className="flex flex-col gap-[10px] min-[900px]:border-b min-[900px]:border-line min-[900px]:p-[18px]">
            <div className="label-mono">players</div>
            {/* WRAPS. A room of sixteen is a supported room, and on a phone this
                was one unwrapping row: every seat shrank until the names were a
                letter wide and the word under them was unreadable. Each row
                carries a floor rather than only a share, so the line takes as
                many as fit at a readable width and starts another. Desktop is a
                column and has never had the problem. */}
            <div
              data-testid="roster"
              className="flex flex-wrap gap-2 min-[900px]:flex-col min-[900px]:flex-nowrap"
            >
              {roster.map((player) => (
                <PlayerRow key={player.id} player={player} locked={lockedBy(player)} />
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* THE ACTION BAR. Phone only: a desktop has the same two controls in the
          column, where there is room for them, and a bar over a 900px layout
          would be a second place to press.

          It is pinned to the bottom of what is VISIBLE rather than of the
          window, because iOS scrolls the layout viewport under a keyboard
          instead of resizing it and `bottom: 0` would put this behind the keys.
          Its height is reserved in the flow above, so it cannot cover the board
          at the bottom of a scroll.

          `coveredHeight` AND NOT `bottomInset`. The two differ by exactly the
          terms that move when the page is scrolled — `visualViewport.offsetTop`,
          which iOS has already applied by carrying fixed elements with the
          visual viewport, and `window.innerHeight`, which is not the box a fixed
          element is laid out in and which changes on its own as Safari's
          toolbars collapse. Applying either of them here is applying it twice,
          and twice is a bar that slides up the screen as the page is scrolled
          up and down it as the page is scrolled down. The reading this uses
          changes when something covers the screen and at no other time. */}
      {isDesktop ? null : (
        <>
          <div aria-hidden="true" style={{ height: ACTION_BAR_HEIGHT }} />
          <div
            data-testid="action-bar"
            data-locked={locked || undefined}
            style={{ bottom: viewport.coveredHeight }}
            className="fixed inset-x-0 z-20 flex gap-[10px] border-t border-line bg-surface px-3 py-2 min-[900px]:hidden"
          >
            {wordCheck ? (
              <>
                {/* Submitting IS checking. It is the press that happens many
                    times a round, so it is the filled one. */}
                <Button
                  variant="primary"
                  size="lg"
                  disabled={!canOffer}
                  onClick={offer}
                  {...keepKeyboard}
                  className="min-w-0 flex-1"
                >
                  Submit
                </Button>
                {locked ? (
                  // A state, not a button, and it has to be unmistakable next to
                  // a filled Submit: doubled green edge, a tick, and the same
                  // word on it. With open answering a lock survives everything,
                  // so this only ever goes back by being pressed again — which
                  // is why it is not offered as a way out.
                  <div
                    data-testid="lock-state"
                    className="flex h-[52px] w-[132px] shrink-0 items-center justify-center gap-2 rounded-[11px] border-2 border-good bg-good/15 text-[15px] leading-none font-bold text-good"
                  >
                    <Icon icon={CheckIcon} size={18} />
                    Done
                  </div>
                ) : (
                  // HELD, NOT TAPPED, and this is the one control on the screen
                  // that has to be. It is a thumb's width from Submit, which is
                  // pressed many times a round; it ends this player's round; and
                  // with open answering it is the press with no way back out —
                  // a lock survives every edit and every further word. That is
                  // the same case the numbers round's `Lock in` is a HoldButton
                  // for, so it is the same control, wearing the same pill and
                  // the same green rather than Submit's filled blue.
                  //
                  // Classic rules keep a plain button, below: there, typing
                  // again retracts the lock, so the mis-press costs a keystroke.
                  //
                  // A hold is a gesture no keyboard can make, so the keyboard
                  // does not have to: `HoldButton` fires on Enter and Space at
                  // once, and the desktop has its own plain `Done` in the
                  // column. The label says the control is held, because it is.
                  <HoldButton
                    onHold={lockIn}
                    keepFocus={fieldFocused}
                    className="h-12 w-[132px] shrink-0 self-center rounded-full border border-good/60 bg-raised px-3 text-[14px] text-ink"
                    // Spelled out, because "Done" alone does not say what is
                    // being declared and this is the press that ends a round.
                    aria-label={
                      answerWord.length > 0
                        ? `Hold to finish, with ${answerWord}`
                        : 'Hold to finish, with no answer'
                    }
                  >
                    Hold to finish
                  </HoldButton>
                )}
              </>
            ) : locked ? (
              // The show's rules: the lock IS the submit, so it takes the whole
              // bar. Typing again retracts it, and this is where that has to be
              // visible — the button comes back the moment a letter lands, a
              // frame before the server agrees.
              <div
                data-testid="lock-state"
                className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[11px] border-2 border-good bg-good/15 text-[16px] leading-none font-bold text-good"
              >
                <Icon icon={CheckIcon} size={18} />
                Locked in
              </div>
            ) : (
              <Button
                variant="primary"
                size="lg"
                disabled={length === 0}
                onClick={lockIn}
                className="min-w-0 flex-1"
              >
                Lock in
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
